/* eslint @typescript-eslint/ban-ts-comment: 0 */

import env from '@beam-australia/react-env';
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import upperCase from 'lodash/upperCase';
import queryString from 'query-string';
import {
  INSTIL_API_VERSION_HEADER,
  INSTIL_AUTH_HEADER,
  INSTIL_CLIENT_SCOPE_HEADER,
} from '@/lib/constants/api';
import {
  AUTH_USER_ID_TOKEN,
  AUTH_X_INSTIL,
  CORRELATION_ID,
  OWNER_ORG_ID_PUB,
  USER_ID_PUB,
  USER_NAME_PUB,
} from '@/lib/constants/localStorageKeys';
import { camelCaseKeys, snakeCaseKeys } from '@/utils/transformKeys';
import buildClientScopeHeader from './buildClientScopeHeader';
import formatAuthBearerHeader from './formatAuthBearerHeader';
import handleData from './handleData';
import handleErrors from './handleErrors';

type ApiVersionNumber = typeof INSTIL_API_VERSION_HEADER;
type AuthHeader = Lowercase<typeof INSTIL_AUTH_HEADER>;
type ClientScopeHeader = Lowercase<typeof INSTIL_CLIENT_SCOPE_HEADER>;

type RequestHeaders = {
  'Content-Type': 'application/json';
  Authorization?: string;
} & Partial<Record<ApiVersionNumber | AuthHeader | ClientScopeHeader, string>>;

/**
 * @example const req = createApiRequest<APIResponse{ foo: Foo }>(...)
 */
export default async function createApiRequest<Data extends object>({
  apiVersion = 2,
  config = undefined,
  data = undefined,
  method = 'post',
  path = '',
  postProcessMethod = camelCaseKeys,
  preProcessMethod = snakeCaseKeys,
  throwErrors = false,
  useAuth = true,
}: {
  apiVersion?: 1 | 2;
  config?: AxiosRequestConfig;
  data?: object;
  method?: ApiMethod;
  path?: string;
  postProcessMethod?: (
    response: APIResponse<Data> | null,
  ) => APIResponse<Data> | undefined;
  preProcessMethod?: (obj: object) => object;
  throwErrors?: boolean;
  useAuth?: boolean;
}) {
  if (!method || !path) {
    throw new Error('Invalid URL or Method');
  }
  const requestConfig = { ...config };
  const authToken = localStorage.getItem(AUTH_USER_ID_TOKEN);
  const instilXAuthToken = localStorage.getItem(AUTH_X_INSTIL);
  const headers: RequestHeaders = {
    'Content-Type': 'application/json',
  };

  if (useAuth && authToken) {
    headers.Authorization = formatAuthBearerHeader(authToken);

    const csId = localStorage.getItem(CORRELATION_ID);
    const userId = localStorage.getItem(USER_ID_PUB);
    const orgId = localStorage.getItem(OWNER_ORG_ID_PUB);
    const userName = localStorage.getItem(USER_NAME_PUB);

    // Append the client scope header
    if (csId && userId && orgId && userName) {
      headers[INSTIL_CLIENT_SCOPE_HEADER.toLowerCase() as ClientScopeHeader] =
        buildClientScopeHeader(userId, orgId, csId, userName);
    }
  }

  if (apiVersion) {
    headers[INSTIL_API_VERSION_HEADER] = `${apiVersion}`;
  }

  // This header is required on all API requests for user informational purposes
  if (instilXAuthToken) {
    headers[INSTIL_AUTH_HEADER.toLowerCase() as AuthHeader] = instilXAuthToken;
  }

  if (typeof requestConfig === 'object') {
    requestConfig.headers = headers;
  }

  const isProd = process.env.NODE_ENV === 'production';
  const apiPath = isProd
    ? `${env('API_URL')}${env('API_PREFIX')}/${path}`
    : `${env('API_PREFIX')}/${path}`;
  const requestParams: [string, AxiosRequestConfig] = [apiPath, requestConfig];

  if (['POST', 'PATCH', 'PUT'].includes(upperCase(method))) {
    // @ts-ignore
    requestParams.splice(1, 0, data ? preProcessMethod(data) : data);
  }

  if (requestParams[1]?.params) {
    requestParams[1].paramsSerializer = (params: object) =>
      queryString.stringify(params, {
        arrayFormat: 'comma',
        skipNull: true,
      });
  }

  return axios[method](...requestParams)
    .then((response: AxiosResponse<APIResponse<Data>>) =>
      postProcessMethod(handleData<APIResponse<Data>>(response)),
    )
    .catch((error) => {
      handleErrors(error);

      // Error API data response if exists
      if (throwErrors) {
        throw camelCaseKeys(error?.response?.data);
      }
    });
}
